import { DatesRangeValue } from '@mantine/dates';
import { Button } from '@zorro/design-system/v2/Button';
import { DatePicker } from '@zorro/design-system/v2/DatePicker';
import {
  formatDateEnLocale,
  formatDateEnLocaleRange,
  parseDateUnknown,
} from '@zorro/shared/formatters';
import { Stack, Text } from '@zorro/web/design-system';
import { useState } from 'react';

import classes from './DateFilter.module.scss';
import { Filter, FilterRecord } from './FilterUtils';

function filterCallback(
  record: Record<string, unknown>,
  filterBy: string,
  datesRange: DatesRangeValue
) {
  const [startDate, endDate] = datesRange;
  const value = record[filterBy];

  // case: record has no date
  if (!value || typeof value !== 'string') {
    return false;
  }

  // 🧠 the value can be formatted in various ways (ISO, En Locale, etc.)
  const parsedDate = parseDateUnknown(value);

  if (!parsedDate.isValid()) {
    return false;
  }

  const parsedEndDate = endDate
    ? parseDateUnknown(endDate).endOf('day')
    : undefined;

  // case: startDate and endDate are set
  if (startDate && endDate) {
    return (
      parsedDate.isSameOrAfter(startDate) &&
      parsedDate.isSameOrBefore(parsedEndDate)
    );
  }

  // case: startDate is set
  if (startDate) {
    return parsedDate.isSameOrAfter(startDate);
  }

  // case: endDate is set
  if (endDate) {
    return parsedDate.isSameOrBefore(parsedEndDate);
  }

  // case: default (no filter)
  return true;
}

type Props<T extends FilterRecord> = {
  filters: Filter<T>[];
  filterBy: string;
  setFilters: (filters: Filter<T>[]) => void;
  /**
   * Called once a full range (both dates) has been selected. Consumers can use
   * this to close the containing filter popover.
   */
  onComplete?: () => void;
};

export const DateFilter = <T extends FilterRecord>({
  filters,
  filterBy,
  setFilters,
  onComplete,
}: Props<T>) => {
  const dateFilter = filters.find(({ type }) => type === filterBy);
  const [localFilter, setLocalFilter] = useState<Filter<T> | undefined>(
    dateFilter
  );

  const datesRange = localFilter?.value as DatesRangeValue | undefined;
  const [firstDate, secondDate] = datesRange || [];

  // Follow the selection state: nothing selected → no label; first date only →
  // "01/01/2020 -"; both dates → "01/01/2020 - 12/31/2020".
  const rangeLabel =
    firstDate && secondDate
      ? formatDateEnLocaleRange(firstDate as Date, secondDate as Date)
      : firstDate
        ? `${formatDateEnLocale(firstDate as Date)} -`
        : null;

  return (
    <Stack className={classes.root}>
      <DatePicker
        type="range"
        value={datesRange}
        allowSingleDateInRange
        onChange={(newDatesRange) => {
          const newFilter: Filter<T> = {
            type: filterBy,
            value: newDatesRange,
            filterCallback: (record) =>
              filterCallback(record, filterBy, newDatesRange),
          };
          setLocalFilter(newFilter);

          const [firstDate, secondDate] = newDatesRange || [];
          if (firstDate && secondDate) {
            const otherFilters = filters.filter(
              ({ type }) => type !== filterBy
            );
            otherFilters.push(newFilter);
            setFilters(otherFilters);
            onComplete?.();
          }
        }}
      />

      {rangeLabel && <Text ta="center">{rangeLabel}</Text>}

      <Button
        size="md"
        variant="tertiary"
        disabled={!datesRange}
        onClick={() => {
          setLocalFilter(undefined);
          setFilters(filters.filter(({ type }) => type !== filterBy));
        }}
      >
        Clear
      </Button>
    </Stack>
  );
};
